import { useEffect } from 'react';

export default function LinkInBio() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://curtiswaters.ai';
    
    // Set page title and meta tags
    document.title = 'Links - Curtis Waters AI';
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Update or create link tags
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.rel = rel;
        document.head.appendChild(element);
      }
      element.href = href;
    };

    // Meta tags
    updateMetaTag('description', 'Connect with Curtis Waters AI. Access our AI business intelligence services, schedule consultations, and learn about deal evaluation solutions.');
    updateMetaTag('keywords', 'Curtis Waters links, AI solutions, business intelligence, contact Curtis Waters');
    updateMetaTag('robots', 'noindex, nofollow');
    
    // Open Graph tags
    updateMetaTag('og:title', 'Links - Curtis Waters AI', true);
    updateMetaTag('og:description', 'Connect with Curtis Waters AI for business intelligence and deal evaluation solutions', true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:url', `${siteUrl}/link-in-bio`, true);
    
    // Twitter tags
    updateMetaTag('twitter:card', 'summary');
    updateMetaTag('twitter:title', 'Links - Curtis Waters AI');
    updateMetaTag('twitter:description', 'Connect with Curtis Waters AI for business intelligence solutions');
    
    // Canonical URL
    updateLinkTag('canonical', `${siteUrl}/link-in-bio`);

    // Add Schema.org structured data
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Links - Curtis Waters AI",
      "description": "Connect with Curtis Waters AI for business intelligence and deal evaluation solutions.",
      "url": `${siteUrl}/link-in-bio`
    };

    const existingScript = document.querySelector('script[data-schema="link-in-bio"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'link-in-bio');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.querySelector('script[data-schema="link-in-bio"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, []);

  const links = [
    {
      title: 'Visit Our Website',
      description: 'Learn about our AI business intelligence solutions',
      url: '/',
      icon: 'ri-home-line',
      color: 'bg-teal-500'
    },
    {
      title: 'Schedule a Consultation',
      description: 'Book your free AI solutions assessment',
      url: '/calendar',
      icon: 'ri-calendar-line',
      color: 'bg-blue-500'
    },
    {
      title: 'Our Services',
      description: 'Explore AI and business intelligence options',
      url: '/#solutions',
      icon: 'ri-service-line',
      color: 'bg-green-500'
    },
    {
      title: 'Contact Us',
      description: 'Get in touch with Curtis Waters AI',
      url: '/#contact',
      icon: 'ri-phone-line',
      color: 'bg-orange-500'
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Profile Section */}
          <div className="text-center mb-12">
            <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center">
              <i className="ri-brain-line text-6xl text-white"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Curtis Waters AI</h1>
            <p className="text-lg text-gray-600 mb-2">AI Business Intelligence Expert</p>
            <p className="text-gray-500">Charlotte, North Carolina</p>
            
            <div className="flex items-center justify-center gap-6 mt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-500">25+</div>
                <div className="text-sm text-gray-600">Years Experience</div>
              </div>
              <div className="w-px h-12 bg-gray-300"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-500">100+</div>
                <div className="text-sm text-gray-600">Companies Served</div>
              </div>
              <div className="w-px h-12 bg-gray-300"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-500">5★</div>
                <div className="text-sm text-gray-600">Rating</div>
              </div>
            </div>
          </div>

          {/* Links Section */}
          <div className="space-y-4">
            {links.map((link, index) => (
              <a
                key={index}
                href={link.url}
                className="block bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:border-teal-200 group cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${link.color} rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                    <i className={`${link.icon} text-2xl text-white`}></i>
                  </div>
                  <div className="flex-grow">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-teal-500 transition-colors">
                      {link.title}
                    </h2>
                    <p className="text-sm text-gray-600">{link.description}</p>
                  </div>
                  <i className="ri-arrow-right-line text-xl text-gray-400 group-hover:text-teal-500 group-hover:translate-x-1 transition-all"></i>
                </div>
              </a>
            ))}
          </div>

          {/* Contact Info */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Get in touch directly</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="tel:+17045645412" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                (704) 564-5412
              </a>
              <button 
                onClick={() => {
                  const user = 'curtis';
                  const domain = 'curtiswaters';
                  const tld = 'com';
                  window.location.href = `mailto:${user}@${domain}.${tld}`;
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line"></i>
                Email
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center text-sm text-gray-500">
            <p>Serving businesses with AI solutions in Charlotte and beyond</p>
            <p className="mt-2">© 2025 Curtis Waters AI. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
}
